// CRA-style service worker registration (production builds only).

import { getServiceWorkerScope, getServiceWorkerUrl } from "./pwaUtils";
import {
  notifyServiceWorkerUpdateAvailable,
  notifyServiceWorkerUpdateInstalled,
} from "../analytics/integrations/pwaIntegration";

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/** @param {ServiceWorkerRegistration} registration */
function listenForServiceWorkerUpdates(registration) {
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    if (!worker) return;

    const isUpdate = Boolean(registration.active);

    worker.addEventListener("statechange", () => {
      if (!isUpdate) return;
      if (worker.state === "installed") {
        notifyServiceWorkerUpdateAvailable("service_worker");
      }
      if (worker.state !== "activated") return;
      notifyServiceWorkerUpdateInstalled("service_worker");
      window.location.reload();
    });
  });
}

function checkForWaitingServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      listenForServiceWorkerUpdates(registration);
      return registration.update();
    })
    .catch(() => {});
}

export function register() {
  if (process.env.NODE_ENV !== "production") return;
  if (!("serviceWorker" in navigator)) return;

  /**
   * RC2: bootstrap() is async (ensureLatestAppShell / cleanup). By the time
   * register() runs, window `load` has often already fired — a load-only
   * listener never ran, so the SW never registered and offline reload blanked.
   */
  const start = () => {
    const swUrl = getServiceWorkerUrl();
    const scope = getServiceWorkerScope();

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, scope);
      return;
    }
    registerValidSW(swUrl, scope);
  };

  if (document.readyState === "complete") {
    start();
  } else {
    window.addEventListener("load", start);
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForWaitingServiceWorker();
    }
  });
}

function registerValidSW(swUrl, scope) {
  navigator.serviceWorker
    .register(swUrl, { scope, updateViaCache: "none" })
    .then((registration) => {
      listenForServiceWorkerUpdates(registration);
      registration.update().catch(() => {});
    })
    .catch(() => {
      /* registration failed — app still works online */
    });
}

function checkValidServiceWorker(swUrl, scope) {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then((response) => {
      if (
        response.status === 404 ||
        !response.headers.get("content-type")?.includes("javascript")
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister();
        });
        return;
      }
      registerValidSW(swUrl, scope);
    })
    .catch(() => {
      /* offline while checking */
    });
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => {});
}
